import { useMemo, useState } from "react";
import { api, fileUrl, dollars } from "@/lib/api";
import { Link } from "react-router-dom";
import { Play, Download, Disc, Music, ShoppingBag, Package, Search, Calendar } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { toast } from "sonner";

/**
 * PurchaseLibrary — the fan's "my music" tab. Backend returns purchases
 * enriched with the track/album object so we can render real cover art,
 * titles, and inline play buttons. Purchases the fan owns are streamed
 * FULL-length (PlayerContext calls /access which honors the purchase).
 *
 * Groups:
 *   • Music (tracks + albums) — playable in-app.
 *   • Merch — shows product name + link to /merch (order tracking WIP).
 *   • Other (subscription, tip, promo) — receipt-only rows.
 */

const KIND_ICON = {
  track: Music,
  album: Disc,
  merch: ShoppingBag,
  bundle: Package,
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function PurchaseLibrary({ purchases }) {
  const { playTrack } = usePlayer();
  const [q, setQ] = useState("");

  const music = useMemo(() =>
    purchases.filter((p) => (p.kind === "track" || p.kind === "album") && p.item), [purchases]);
  const merch = useMemo(() =>
    purchases.filter((p) => p.kind === "merch" || p.kind === "bundle"), [purchases]);
  const other = useMemo(() =>
    purchases.filter((p) => !["track", "album", "merch", "bundle"].includes(p.kind)), [purchases]);

  const query = q.trim().toLowerCase();
  const matches = (p) => {
    if (!query) return true;
    const t = p.item?.title || p.label || "";
    const a = p.item?.artist_name || "";
    return `${t} ${a}`.toLowerCase().includes(query);
  };

  const musicFiltered = music.filter(matches);
  const merchFiltered = merch.filter(matches);

  const downloadTrack = async (trackId) => {
    // Uses the auth cookie so the download endpoint knows the buyer.
    try {
      const res = await api.get(`/tracks/${trackId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed — please refresh and try again");
    }
  };

  const playPurchase = (p) => {
    if (p.kind === "track" && p.item) {
      playTrack(p.item, [p.item]);
    } else if (p.kind === "album" && p.item?.tracks?.length) {
      playTrack(p.item.tracks[0], p.item.tracks);
    }
  };

  if (purchases.length === 0) {
    return (
      <div data-testid="purchase-library-empty" className="border border-[#2A2A2A] p-12 text-center bg-[#121212]">
        <Music className="h-8 w-8 text-gray-600 mx-auto mb-3" />
        <div className="font-display text-xl mb-2">Your library is empty.</div>
        <p className="font-mono text-xs text-gray-500 mb-5">
          Every track and album you buy shows up here — playable full-length, downloadable, and safely yours.
        </p>
        <Link to="/browse" className="inline-block bg-[#FF4500] text-black px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-[#E53E00] transition-colors">
          Browse the vault
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="purchase-library" className="space-y-8">
      {/* Search */}
      {(music.length + merch.length) > 3 && (
        <div className="relative max-w-md">
          <Search className="h-4 w-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="library-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your library..."
            className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#FF4500] outline-none py-2.5 pl-10 pr-3 font-mono text-xs uppercase tracking-widest"
          />
        </div>
      )}

      {/* MUSIC */}
      {musicFiltered.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500]">
              Music
            </div>
            <div className="font-mono text-[10px] text-gray-500">{musicFiltered.length} owned</div>
          </div>
          <div className="border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
            {musicFiltered.map((p) => <LibraryRow key={p.id} p={p} onPlay={playPurchase} onDownload={downloadTrack} />)}
          </div>
        </section>
      )}

      {/* MERCH */}
      {merchFiltered.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500]">Merch</div>
            <div className="font-mono text-[10px] text-gray-500">{merchFiltered.length} ordered</div>
          </div>
          <div className="border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
            {merchFiltered.map((p) => <MerchRow key={p.id} p={p} />)}
          </div>
        </section>
      )}

      {/* OTHER (subscription, tip, promo) */}
      {other.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500]">Receipts</div>
            <div className="font-mono text-[10px] text-gray-500">{other.length}</div>
          </div>
          <div className="border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
            {other.map((p) => (
              <div key={p.id} className="grid grid-cols-[auto_1fr_auto] gap-4 items-center px-4 py-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 w-24">{p.kind}</div>
                <div className="font-mono text-xs text-gray-400 truncate">{p.label || p.description || p.item_id || "—"}</div>
                <div className="font-mono text-xs text-[#FF4500] whitespace-nowrap">{dollars(p.amount_cents)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {musicFiltered.length === 0 && merchFiltered.length === 0 && query && (
        <div className="font-mono text-xs text-gray-500 text-center py-8">
          No purchases match "{q}".
        </div>
      )}
    </div>
  );
}

function LibraryRow({ p, onPlay, onDownload }) {
  const item = p.item;
  const Icon = KIND_ICON[p.kind] || Music;
  const cover = item?.cover_path ? fileUrl(item.cover_path) : "";
  const isAlbum = p.kind === "album";

  return (
    <div
      data-testid={`library-row-${p.id}`}
      className="grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1fr_auto_auto_auto] gap-3 md:gap-4 items-center px-3 md:px-4 py-3 hover:bg-white/[0.02] transition-colors"
    >
      {/* Cover */}
      <div className="relative h-14 w-14 shrink-0 bg-[#1a1a1a] border border-white/5 overflow-hidden">
        {cover ? (
          <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center">
            <Icon className="h-5 w-5 text-gray-600" />
          </div>
        )}
        <button
          data-testid={`library-play-${p.id}`}
          onClick={() => onPlay(p)}
          className="absolute inset-0 grid place-items-center bg-black/60 opacity-0 hover:opacity-100 transition-opacity"
          aria-label="Play"
        >
          <Play className="h-5 w-5 text-[#FF4500]" fill="currentColor" />
        </button>
      </div>

      {/* Meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-widest bg-[#FF4500]/15 text-[#FF4500] px-1.5 py-0.5 shrink-0">
            {isAlbum ? "Album" : "Track"}
          </span>
          <Link
            to={isAlbum ? `/album/${item.id}` : (item?.album_id ? `/album/${item.album_id}` : "#")}
            className="font-display font-bold text-sm md:text-base truncate hover:text-[#FF4500] transition-colors"
          >
            {item?.title || p.item_id}
          </Link>
        </div>
        <Link
          to={item?.artist_id ? `/artist/${item.artist_id}` : "#"}
          className="font-mono text-[11px] text-gray-400 truncate hover:text-white transition-colors"
        >
          {item?.artist_name || "—"}
          {isAlbum && item?.tracks?.length ? <span className="text-gray-600"> · {item.tracks.length} tracks</span> : null}
        </Link>
      </div>

      {/* Date (hidden on mobile) */}
      <div className="hidden md:flex items-center gap-1.5 font-mono text-[10px] text-gray-500 whitespace-nowrap">
        <Calendar className="h-3 w-3" /> {fmtDate(p.created_at)}
      </div>

      {/* Price */}
      <div className="font-mono text-xs text-[#FF4500] whitespace-nowrap tabular-nums">
        {dollars(p.amount_cents)}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <button
          data-testid={`library-play-btn-${p.id}`}
          onClick={() => onPlay(p)}
          className="h-8 w-8 grid place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
          title="Play"
        >
          <Play className="h-3.5 w-3.5" fill="currentColor" />
        </button>
        {p.kind === "track" && (
          <button
            data-testid={`library-dl-${p.item_id}`}
            onClick={() => onDownload(p.item_id)}
            className="h-8 w-8 grid place-items-center border border-white/10 hover:border-[#FF4500] hover:text-[#FF4500] text-gray-300 transition-colors"
            title="Download MP3"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function MerchRow({ p }) {
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3">
      <ShoppingBag className="h-5 w-5 text-[#FF4500]" />
      <div className="min-w-0">
        <div className="font-display font-bold text-sm truncate">{p.label || "MeloCraft Merch"}</div>
        <div className="font-mono text-[10px] text-gray-500 truncate">
          Ordered {fmtDate(p.created_at)}{p.printful_order_id ? ` · order #${p.printful_order_id}` : ""}
        </div>
      </div>
      <div className="hidden sm:block font-mono text-[10px] uppercase tracking-widest text-gray-400">
        {p.printful_status || "processing"}
      </div>
      <div className="font-mono text-xs text-[#FF4500] whitespace-nowrap tabular-nums">
        {dollars(p.amount_cents)}
      </div>
    </div>
  );
}
